import {
  CheckCircleOutline,
  ErrorOutline,
  WarningAmberOutlined,
  InfoOutlined,
  HelpOutlineOutlined,
  Close,
} from "@mui/icons-material";
import {
  Snackbar,
  Box,
  Typography,
  IconButton,
  Button,
  Slide,
  SlideProps,
} from "@mui/material";
import React, { createContext, useContext, useState, useCallback } from "react";

/**
 * Global Toast Context
 *
 * Provides toast notifications throughout the application.
 * New toasts immediately replace any existing toast (no queueing).
 *
 * Features:
 * - 5 types: question, info, warning, success, error
 * - Title, description, and extra link support
 * - Action buttons with callbacks
 * - Custom styling with left border and background colors
 * - Close button in top right
 * - Immediate replacement of existing toasts
 */

// Toast Types
export type ToastType = "question" | "info" | "warning" | "success" | "error";

// Types
export interface ToastMessage {
  id: string;
  type: ToastType;
  title?: string;
  description?: string;
  extraLink?: React.ReactNode;
  duration?: number;
  action?: string;
  onAction?: () => void;
}

export interface ToastOptions {
  duration?: number;
  action?: string;
  onAction?: () => void;
}

interface ToastContextType {
  showQuestion: (
    title: string,
    description?: string,
    extraLink?: React.ReactNode,
    options?: ToastOptions,
  ) => void;
  showInfo: (
    title: string,
    description?: string,
    extraLink?: React.ReactNode,
    options?: ToastOptions,
  ) => void;
  showWarning: (
    title: string,
    description?: string,
    extraLink?: React.ReactNode,
    options?: ToastOptions,
  ) => void;
  showSuccess: (
    title: string,
    description?: string,
    extraLink?: React.ReactNode,
    options?: ToastOptions,
  ) => void;
  showError: (
    title: string,
    description?: string,
    extraLink?: React.ReactNode,
    options?: ToastOptions,
  ) => void;
  hideToast: () => void;
}

// Toast type configurations
const toastConfig = {
  question: {
    icon: HelpOutlineOutlined,
    borderColor: "#6366f1", // Indigo
    backgroundColor: "#f0f0ff",
  },
  info: {
    icon: InfoOutlined,
    borderColor: "#06b6d4", // Cyan
    backgroundColor: "#ecfeff",
  },
  warning: {
    icon: WarningAmberOutlined,
    borderColor: "#f59e0b", // Amber
    backgroundColor: "#fffbeb",
  },
  success: {
    icon: CheckCircleOutline,
    borderColor: "#10b981", // Emerald
    backgroundColor: "#ecfdf5",
  },
  error: {
    icon: ErrorOutline,
    borderColor: "#ef4444", // Red
    backgroundColor: "#fef2f2",
  },
};

// Slide transition component
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

// Context
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Provider Props
interface ToastProviderProps {
  children: React.ReactNode;
}

// Default toast duration
const DEFAULT_DURATION = 2000;

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [currentToast, setCurrentToast] = useState<ToastMessage | null>(null);

  // Generate unique ID for each toast
  const generateId = useCallback(() => {
    return Date.now().toString(36) + Math.random().toString(36).slice(2);
  }, []);

  // Show toast immediately (replaces any existing toast)
  const showToast = useCallback(
    (
      type: ToastType,
      title: string,
      description?: string,
      extraLink?: React.ReactNode,
      options?: ToastOptions,
    ) => {
      const newToast: ToastMessage = {
        id: generateId(),
        type,
        title,
        description,
        extraLink,
        duration: options?.duration ?? DEFAULT_DURATION,
        action: options?.action,
        onAction: options?.onAction,
      };

      // Immediately show the new toast, replacing any existing one
      setCurrentToast(newToast);
    },
    [generateId],
  );

  // Hide current toast
  const hideToast = useCallback(() => {
    setCurrentToast(null);
  }, []);

  // Toast methods
  const showQuestion = useCallback(
    (
      title: string,
      description?: string,
      extraLink?: React.ReactNode,
      options?: ToastOptions,
    ) => {
      showToast("question", title, description, extraLink, options);
    },
    [showToast],
  );

  const showInfo = useCallback(
    (
      title: string,
      description?: string,
      extraLink?: React.ReactNode,
      options?: ToastOptions,
    ) => {
      showToast("info", title, description, extraLink, options);
    },
    [showToast],
  );

  const showWarning = useCallback(
    (
      title: string,
      description?: string,
      extraLink?: React.ReactNode,
      options?: ToastOptions,
    ) => {
      showToast("warning", title, description, extraLink, options);
    },
    [showToast],
  );

  const showSuccess = useCallback(
    (
      title: string,
      description?: string,
      extraLink?: React.ReactNode,
      options?: ToastOptions,
    ) => {
      showToast("success", title, description, extraLink, options);
    },
    [showToast],
  );

  const showError = useCallback(
    (
      title: string,
      description?: string,
      extraLink?: React.ReactNode,
      options?: ToastOptions,
    ) => {
      showToast("error", title, description, extraLink, options);
    },
    [showToast],
  );

  // Handle close event
  const handleClose = useCallback(
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") {
        return;
      }
      hideToast();
    },
    [hideToast],
  );

  // Handle action button click
  const handleActionClick = useCallback(() => {
    if (currentToast?.onAction) {
      currentToast.onAction();
    }
    hideToast();
  }, [currentToast, hideToast]);

  const contextValue: ToastContextType = {
    showQuestion,
    showInfo,
    showWarning,
    showSuccess,
    showError,
    hideToast,
  };

  // Get toast configuration
  const config = currentToast ? toastConfig[currentToast.type] : null;
  const IconComponent = config?.icon;

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {/* Global Snackbar */}
      <Snackbar
        open={!!currentToast}
        autoHideDuration={currentToast?.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        slots={{
          transition: SlideTransition,
        }}
        sx={{
          "& .MuiSnackbarContent-root": {
            padding: 0,
          },
        }}
      >
        {currentToast && config && IconComponent ? (
          <Box
            sx={{
              display: "flex",
              minWidth: "320px",
              maxWidth: "480px",
              backgroundColor: config.backgroundColor,
              borderLeft: `4px solid ${config.borderColor}`,
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
            }}
          >
            {/* Main Content */}
            <Box
              sx={{
                display: "flex",
                flex: 1,
                p: 2,
                gap: 1.5,
              }}
            >
              {/* Icon */}
              <IconComponent
                sx={{
                  color: config.borderColor,
                  fontSize: "20px",
                  mt: 0.25,
                  flexShrink: 0,
                }}
              />

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Title */}
                {currentToast.title && (
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      mb: currentToast.description ? 0.5 : 0,
                      lineHeight: 1.4,
                    }}
                  >
                    {currentToast.title}
                  </Typography>
                )}

                {/* Description */}
                {currentToast.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.4,
                      mb: currentToast.extraLink ? 1 : 0,
                    }}
                  >
                    {currentToast.description}
                  </Typography>
                )}

                {/* Extra Link */}
                {currentToast.extraLink && (
                  <Box sx={{ mt: 1 }}>{currentToast.extraLink}</Box>
                )}

                {/* Action Button */}
                {currentToast.action && (
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleActionClick}
                      sx={{
                        borderColor: config.borderColor,
                        color: config.borderColor,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "13px",
                        "&:hover": {
                          backgroundColor: config.borderColor,
                          color: "white",
                        },
                      }}
                    >
                      {currentToast.action}
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Close Button */}
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  color: "text.secondary",
                  alignSelf: "flex-start",
                  p: 0.5,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
};

// Custom hook
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export default ToastProvider;
